"use client";

import { FormEvent, useState } from "react";
import { Send as SendIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { countryCodes } from "@/components/vitalz-pro/BrochureDownloadButton";
import { contact } from "@/content/contact";
import {
  formatMobileNumber,
  getEmailError,
  getMobileNumberError,
} from "@/lib/formValidation";

type Status = "idle" | "loading" | "success" | "error";

const initial = {
  name: "",
  organisation: "",
  role: "",
  email: "",
  countryCode: "+91",
  contactNumber: "",
  workMode: "",
  message: "",
};

const WEB3FORMS_ACCESS_KEY =
  process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY ??
  "672f1e21-f39d-4500-bc21-9b3af7b2ae78";
export function ContactForm() {
  const [values, setValues] = useState(initial);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const formData = new FormData(event.currentTarget);
    const botcheck = String(formData.get("botcheck") || "");

    const nextEmailError = getEmailError(values.email);
    if (nextEmailError) {
      setEmailError(nextEmailError);
      return;
    }

    const mobileNumberError = getMobileNumberError(
      values.countryCode,
      values.contactNumber,
    );
    if (mobileNumberError) {
      setPhoneError(mobileNumberError);
      return;
    }

    setStatus("loading");

    try {
      const selectedWorkMode = contact.workOptions.find(
        (option) => option.value === values.workMode,
      );
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New contact enquiry from HealthVoitho.com",
          from_name: "HealthVoitho Website",
          name: values.name,
          organisation: values.organisation,
          role: values.role,
          email: values.email,
          contact_number: formatMobileNumber(
            values.countryCode,
            values.contactNumber,
          ),
          how_we_might_work: selectedWorkMode?.label ?? "Not specified",
          message: values.message,
          botcheck,
        }),
      });
      const data = (await res.json()) as { success?: boolean; message?: string };

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Something went wrong.");
      }

      setStatus("success");
      setValues(initial);
      setEmailError("");
      setPhoneError("");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  }

  return (
    <div>
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-[1.5rem] bg-[#eff5f3] px-6 py-10 md:px-8"
          >
            <p className="font-display text-2xl text-[#1d315f]">{contact.success}</p>
            <button
              type="button"
              className="mt-6 text-sm font-medium text-[#397d93] hover:underline"
              onClick={() => setStatus("idle")}
            >
              Send another message
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="grid gap-6"
          >
            <input
              type="checkbox"
              name="botcheck"
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />
            <Field
              label="Name"
              name="name"
              autoComplete="name"
              placeholder="Enter your full name"
              required
              value={values.name}
              onChange={(v) => setValues((s) => ({ ...s, name: v }))}
            />
            <Field
              label="Organisation"
              name="organisation"
              autoComplete="organization"
              placeholder="Enter your organisation name"
              required
              value={values.organisation}
              onChange={(v) => setValues((s) => ({ ...s, organisation: v }))}
            />
            <Field
              label="Role"
              name="role"
              autoComplete="organization-title"
              placeholder="Enter your role"
              value={values.role}
              onChange={(v) => setValues((s) => ({ ...s, role: v }))}
            />
            <Field
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="Enter your email address"
              required
              value={values.email}
              error={emailError}
              onBlur={() => setEmailError(getEmailError(values.email))}
              onChange={(v) => {
                setValues((s) => ({ ...s, email: v }));
                if (emailError) setEmailError("");
              }}
            />
            <fieldset>
              <legend className="text-sm font-medium text-[#1d315f]">
                Contact number
              </legend>
              <div className="mt-2.5 grid grid-cols-[7.25rem_1fr] gap-2">
                <label className="sr-only" htmlFor="contact-country-code">
                  Country code
                </label>
                <select
                  id="contact-country-code"
                  name="countryCode"
                  required
                  value={values.countryCode}
                  onChange={(event) => {
                    const countryCode = event.target.value;
                    setValues((current) => ({
                      ...current,
                      countryCode,
                    }));
                    setPhoneError(
                      getMobileNumberError(countryCode, values.contactNumber),
                    );
                  }}
                  className="min-w-0 rounded-[1rem] border border-[#b9cdcd] bg-white/90 px-3 py-3.5 text-sm text-[#263d43] outline-none transition focus:border-[#468397] focus:ring-4 focus:ring-[#468397]/10"
                >
                  {countryCodes.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>

                <label className="sr-only" htmlFor="contact-phone-number">
                  Mobile number
                </label>
                <input
                  id="contact-phone-number"
                  name="contactNumber"
                  type="tel"
                  autoComplete="tel-national"
                  inputMode="numeric"
                  pattern="[0-9]+"
                  title="Enter numbers only"
                  placeholder="Enter your mobile number"
                  required
                  aria-invalid={Boolean(phoneError)}
                  aria-describedby={phoneError ? "contact-phone-error" : undefined}
                  value={values.contactNumber}
                  onBlur={() =>
                    setPhoneError(
                      getMobileNumberError(
                        values.countryCode,
                        values.contactNumber,
                      ),
                    )
                  }
                  onChange={(event) => {
                    setValues((current) => ({
                      ...current,
                      contactNumber: event.target.value
                        .replace(/\D/g, "")
                        .slice(0, 15),
                    }));
                    if (phoneError) setPhoneError("");
                  }}
                  className="min-w-0 rounded-[1rem] border border-[#b9cdcd] bg-white/90 px-4 py-3.5 text-sm text-[#263d43] outline-none transition placeholder:text-[#708185] focus:border-[#468397] focus:ring-4 focus:ring-[#468397]/10"
                />
              </div>
              {phoneError ? (
                <p
                  id="contact-phone-error"
                  className="mt-2 text-sm text-accent"
                  role="alert"
                >
                  {phoneError}
                </p>
              ) : null}
            </fieldset>

            <label className="block">
              <span className="text-sm font-medium text-[#1d315f]">How we might work</span>
              <select
                name="workMode"
                className="mt-2.5 w-full rounded-[1rem] border border-[#b9cdcd] bg-white/90 px-4 py-3.5 text-sm text-[#263d43] outline-none transition focus:border-[#468397] focus:ring-4 focus:ring-[#468397]/10"
                value={values.workMode}
                onChange={(e) =>
                  setValues((s) => ({ ...s, workMode: e.target.value }))
                }
              >
                <option value="">Select an option</option>
                {contact.workOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-[#1d315f]">Message</span>
              <textarea
                name="message"
                placeholder="Enter your message"
                required
                rows={6}
                className="mt-2.5 w-full resize-y rounded-[1rem] border border-[#b9cdcd] bg-white/90 px-4 py-3.5 text-sm text-[#263d43] outline-none transition placeholder:text-[#708185] focus:border-[#468397] focus:ring-4 focus:ring-[#468397]/10"
                value={values.message}
                onChange={(e) =>
                  setValues((s) => ({ ...s, message: e.target.value }))
                }
              />
            </label>

            {error ? (
              <p className="text-sm text-accent" role="alert">
                {error}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={status === "loading"}
              className="hero-outline-action group inline-flex items-center gap-3 justify-self-start rounded-full border-[1.5px] border-transparent px-7 py-4 text-sm font-semibold disabled:pointer-events-none disabled:opacity-60"
            >
              {status === "loading" ? "Sending…" : "Send"}
              <SendIcon
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                aria-hidden="true"
              />
            </button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  required,
  type = "text",
  autoComplete,
  inputMode,
  pattern,
  placeholder,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  inputMode?: "email" | "numeric" | "search" | "tel" | "text" | "url";
  pattern?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#1d315f]">{label}</span>
      <input
        name={name}
        type={type}
        autoComplete={autoComplete}
        inputMode={inputMode}
        pattern={pattern}
        placeholder={placeholder}
        required={required}
        aria-invalid={Boolean(error)}
        className="mt-2.5 w-full rounded-[1rem] border border-[#b9cdcd] bg-white/90 px-4 py-3.5 text-sm text-[#263d43] outline-none transition placeholder:text-[#708185] focus:border-[#468397] focus:ring-4 focus:ring-[#468397]/10"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      {error ? (
        <span className="mt-2 block text-sm text-accent" role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
}
